import type { JsonValue, RecordForm } from '../protocol/ui';

export function initialRecord(form: RecordForm): JsonValue {
  switch (form.type) {
    case 'fixed': return structuredClone(form.value);
    case 'boolean': return false;
    case 'integer': return form.min;
    case 'pick': return form.options[0]?.value ?? '';
    case 'text': return '';
    case 'array': return Array.from({ length: form.min }, () => initialRecord(form.items));
    case 'object': return Object.fromEntries(form.fields.filter(f => f.required).map(f => [f.name!, initialRecord(f)]));
  }
}

export function validateRecord(form: RecordForm, value: unknown): string | null {
  const fail = (message: string) => `${form.label}: ${message}`;
  switch (form.type) {
    case 'fixed': return JSON.stringify(value) === JSON.stringify(form.value) ? null : fail('use the supplied value.');
    case 'boolean': return typeof value === 'boolean' ? null : fail('choose yes or no.');
    case 'integer': return Number.isInteger(value) && Number(value) >= form.min && Number(value) <= form.max ? null : fail(`enter a whole number from ${form.min} to ${form.max}.`);
    case 'pick': return form.options.some(o => o.value === value) ? null : fail('choose a listed option.');
    case 'text': return typeof value === 'string' && value.length <= form.maxLength ? null : fail(`enter at most ${form.maxLength} characters.`);
    case 'array': {
      if (!Array.isArray(value) || value.length < form.min || value.length > form.max) return fail(`choose ${form.min} to ${form.max} entries.`);
      for (const item of value) { const problem = validateRecord(form.items, item); if (problem) return problem; }
      return null;
    }
    case 'object': {
      if (!value || typeof value !== 'object' || Array.isArray(value)) return fail('fill in its fields.');
      const data = value as Record<string, unknown>;
      if (Object.keys(data).some(k => !form.fields.some(f => f.name === k))) return fail('unknown field.');
      for (const field of form.fields) {
        if (!(field.name! in data)) { if (field.required) return fail(`include ${field.label}.`); continue; }
        const problem = validateRecord(field, data[field.name!]); if (problem) return problem;
      }
      return null;
    }
  }
}

/** Human-readable review, with option labels instead of raw instance IDs. */
export function describeRecord(form: RecordForm, value: JsonValue): string {
  if (form.type === 'pick') return form.options.find(o => o.value === value)?.label ?? 'No selection';
  if (form.type === 'array') return (value as JsonValue[]).map(v => describeRecord(form.items, v)).join('; ') || 'none';
  if (form.type === 'object') return form.fields.filter(f => f.name! in (value as object)).map(f => `${f.label}: ${describeRecord(f, (value as Record<string, JsonValue>)[f.name!]!)}`).join(', ');
  return typeof value === 'object' ? 'Supplied values' : String(value);
}
